import { useState, useEffect } from 'react';

/**
 * Hook that detects the keyboard layout by observing keydown events.
 * Returns a string identifier for the layout (e.g. 'fr-fr', 'en-us', 'de-de').
 * 
 * Note: Layout detection relies on the user typing specific keys (Q, A, Z, W, M, Y).
 * Until a distinguishing key is pressed, the layout may remain null or 'unknown'.
 */
export function useKeyboardLayout() {
    const [layout, setLayout] = useState(() => {
        if (typeof window !== 'undefined') {
            const initial = navigator.language || 'unknown';
            window.currentKeyLayout = window.currentKeyLayout || initial;
            return window.currentKeyLayout;
        }
        return 'unknown';
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.currentKeyLayout = layout;
        }
    }, [layout]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            const { key, code } = event;

            // Ignore modifiers / non-printable checks if needed, but key/code usually suffice.

            // AZERTY (fr-fr) vs QWERTY (en-us) checks
            // ----------------------------------------------------------------------
            // KeyQ: 'a' on AZERTY, 'q' on QWERTY
            if (code === 'KeyQ') {
                if (key === 'a') setLayout('fr-fr');
                else if (key === 'q') setLayout('en-us');
            }

            // KeyA: 'q' on AZERTY, 'a' on QWERTY
            if (code === 'KeyA') {
                if (key === 'q') setLayout('fr-fr');
                else if (key === 'a') setLayout('en-us');
            }

            // KeyW: 'z' on AZERTY, 'w' on QWERTY
            if (code === 'KeyW') {
                if (key === 'z') setLayout('fr-fr');
                else if (key === 'w') setLayout('en-us');
            }

            // KeyZ: 'w' on AZERTY, 'z' on QWERTY, 'y' on QWERTZ
            if (code === 'KeyZ') {
                if (key === 'w') setLayout('fr-fr');
                else if (key === 'z') setLayout('en-us');
                else if (key === 'y') setLayout('de-de');
            }

            // KeyY: 'y' on QWERTY/AZERTY, 'z' on QWERTZ
            if (code === 'KeyY') {
                if (key === 'z') setLayout('de-de');
                else if (key === 'y' && layout === 'de-de') setLayout('en-us'); // fallback if previously guessed de-de wrongly? 
                // Note: 'y' is same on en-us and fr-fr, so Y alone doesn't distinguish them well without context
            }

            // Special check for AZERTY 'M' placement
            // On US QWERTY, 'm' is at KeyM.
            // On FR AZERTY, ',' is at KeyM. 'm' is at KeySemicolon (right of L).
            if (code === 'KeyM') {
                if (key === ',') setLayout('fr-fr');
                else if (key === 'm') setLayout('en-us'); // or generic qwerty
            }
            if (code === 'Comma') {
                if (key === ';') setLayout('fr-fr');
                else if (key === ',') setLayout('en-us'); // or generic qwerty
            }

            // Check for AZERTY digit row special characters (Direct access on FR layout)
            if (['&', 'é', '"', "'", '(', '-', 'è', '_', 'ç', 'à'].includes(key) && code.startsWith('Digit')) {
                setLayout('fr-fr');
            }
            // console.log("key: ", key, "code: ", code);

        };

        window.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [layout]);

    return layout;
}
